import React from "react";
import { Button } from "@/components/catalyst-ui/button";
import { Heading } from "@/components/catalyst-ui/heading";
import { Text } from "@/components/catalyst-ui/text";
import { Card } from "@/components/catalyst-ui/card";
import { useActiveWorkspace } from "@/state/selection.store";
import posthogAnalytics from "@/lib/posthog";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to monitoring service (e.g., Sentry)
    console.error("Error caught by boundary:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Track error in PostHog
    try {
      posthogAnalytics.track("error_caught_by_boundary", {
        error_type: "component_error",
        error_boundary: "ErrorBoundary",
        error_message: error?.message || "Unknown error",
        error_name: error?.name || "Error",
        error_stack: error?.stack?.substring(0, 500) || "", // Limit stack trace length
        component_stack: errorInfo?.componentStack?.substring(0, 500) || "",
        page: window.location.pathname,
        url: window.location.href,
        user_agent: navigator.userAgent,
      });
    } catch (trackingError) {
      console.error("Failed to track error in PostHog:", trackingError);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo);
      }

      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }) {
  const { workspaceId } = useActiveWorkspace();

  // Always log errors to console
  if (error) {
    console.error("ErrorBoundary Error:", error);
    if (error.stack) {
      console.error("Error Stack:", error.stack);
    }
  }

  const handleReload = () => {
    // Track reload action
    posthogAnalytics.track("error_user_action", {
      action_type: "reload",
      error_severity: "component",
      error_message: error?.message || "Unknown error",
      page: window.location.pathname,
    });
    window.location.reload();
  };

  const handleGoHome = () => {
    const homeUrl = workspaceId ? `/?workspace=${workspaceId}` : "/";
    // Track go home action
    posthogAnalytics.track("error_user_action", {
      action_type: "navigate_home",
      error_severity: "component",
      error_message: error?.message || "Unknown error",
      page: window.location.pathname,
      destination: homeUrl,
    });
    window.location.href = homeUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-md w-full shadow-[0px_18px_45px_0px_#00000026] !rounded-xl">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="space-y-2">
            <Heading
              level={2}
              className="!text-lg !font-semibold !text-[#09090B]"
            >
              Something went wrong
            </Heading>
            <Text className="!text-sm !font-medium !text-[#71717A]">
              We encountered an unexpected error. Please try refreshing the page
              or go back to the dashboard.
            </Text>
          </div>

          {import.meta.env.DEV && error && (
            <details className="mt-4 w-full text-left">
              <summary className="cursor-pointer text-sm font-medium text-[#09090B] hover:text-[#52525B] data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded">
                Error Details (Development)
              </summary>
              <pre className="mt-2 text-xs bg-[#F4F4F5] border border-[#E4E4E7] p-3 rounded overflow-auto max-h-32 text-[#09090B]">
                {error.toString()}
              </pre>
            </details>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full">
            <Button
              color="dark"
              onClick={handleReload}
              className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            >
              Reload Page
            </Button>
            <Button
              outline
              onClick={handleGoHome}
              className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
            >
              Go Home
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ErrorBoundary;
